/**
 * L2 selection controls — SearchField · OptionSelect.
 *
 * The pill TAB bar lives in `modes.jsx` now: it and the segmented control were
 * the same component (both route children by `region`), so they merged into
 * `ModeSwitch`.
 *
 * These are the blueprint's UI lane: each owns its own selection state and
 * emits ONE signal (`textChanged` / `changed`). Nothing here reaches into a
 * grid — the blueprint's `connections` decide which slot the signal lands in,
 * which is exactly what makes the same picker reusable across widgets.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { TYPE_STYLE, resetButton, hitTarget, toneColor, gapValue, useSelection, POSTER_ASPECT } from './ui';
import { Image } from './primitives';
import { PosterFace } from './cards';
import { contentPadding } from '../layout';

/**
 * An option's `meta` — the ONE marker slot the option shape was missing.
 *
 * A catalogue option already carries what kind of thing it is (a still or a
 * clip, how long it runs, what it costs), and the picker had nowhere to draw
 * it: `{key, label, desc?, media?, value?, payload?}` has a description and a
 * label and no marker. So a rail could show two tiles that behave completely
 * differently and look identical.
 *
 * A string is the marker; `{text, tone}` is a marker that also carries a
 * status colour. It draws as the poster's EYEBROW (over the picture) and as a
 * caption eyebrow above the label — the same reading in both card layouts.
 */
function optionMeta(o) {
  const raw = o?.meta;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') {
    const t = raw.text == null ? '' : String(raw.text);
    return t ? { text: t, tone: raw.tone } : null;
  }
  return { text: String(raw), tone: undefined };
}

/**
 * 只接受蓝图声明的十六进制色值，避免把任意字符串写入内联样式。
 */
function optionSwatch(value) {
  const swatch = typeof value === 'string' ? value.trim() : '';
  return /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(swatch) ? swatch : null;
}

// A multi-select bar's "nothing chosen" — a constant, so the default does not
// hand `useSelection` a fresh array on every render.
const EMPTY_KEYS = [];

/**
 * SEALED: debounce + a clear affordance. Emits `textChanged` (latest wins).
 *
 * The `collapsible` variant is the same field, folded: an icon until it is
 * opened (by its own tap or by an `open` slot — a host nav action, a trailing
 * chip), then a full-width field with a Cancel that CLEARS as it closes. That
 * last part is the seal: a collapsed search that quietly keeps filtering is a
 * page the user cannot get back.
 */
export function SearchField({ variant, props = {}, emit, slots }) {
  const [q, setQ] = useState('');
  const timer = useRef(null);
  const setQuery = slots?.setQuery;
  const openSlot = slots?.open;
  const collapsible = (props.variant || variant) === 'collapsible';
  const [open, setOpen] = useState(!collapsible);
  const inputRef = useRef(null);

  useEffect(() => {
    if (setQuery?.payload?.query !== undefined) setQ(String(setQuery.payload.query || ''));
  }, [setQuery]);

  // The host (or a trailing chip) opens it; focus follows, because a search
  // that opens without the caret in it costs a second tap every time.
  useEffect(() => {
    if (!collapsible || !openSlot) return;
    setOpen(true);
    inputRef.current?.focus();
  }, [openSlot, collapsible]);

  // The debounce window lives here so EVERY widget gets the same feel; a
  // connection's `rate: debounce(250)` is the data lane's own guard on top.
  const push = (next) => {
    setQ(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => emit?.('textChanged', { query: next }), 180);
  };

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const close = () => {
    setOpen(false);
    setQ('');
    emit?.('cleared', {});
    emit?.('textChanged', { query: '' });
  };

  if (collapsible && !open) {
    return (
      <button
        type="button"
        aria-label={props.placeholder || 'Search'}
        aria-expanded={false}
        onClick={() => { setOpen(true); requestAnimationFrame(() => inputRef.current?.focus()); }}
        style={{
          ...resetButton, ...hitTarget,
          width: 40, height: 40, borderRadius: 'var(--r-pill)', flex: '0 0 auto',
          background: 'var(--surface-3)', color: 'var(--text-2)', fontSize: 15,
          display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 40,
        }}
      >⌕</button>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0,
        background: 'var(--surface-3)', borderRadius: 'var(--r-pill)',
        padding: '0 var(--sp-3)', height: 40,
      }}>
        <span style={{ color: 'var(--text-3)', fontSize: 15 }}>⌕</span>
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => push(e.target.value)}
          placeholder={props.placeholder || 'Search'}
          aria-label={props.placeholder || 'Search'}
          style={{
            flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent',
            font: 'inherit', fontSize: 'var(--t-md)', color: 'var(--text)',
          }}
        />
        {q ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => { setQ(''); emit?.('cleared', {}); emit?.('textChanged', { query: '' }); }}
            style={{ ...resetButton, color: 'var(--text-3)', fontSize: 14, padding: 4 }}
          >✕</button>
        ) : null}
      </div>
      {collapsible ? (
        <button
          type="button"
          onClick={close}
          style={{ ...resetButton, color: 'var(--accent)', fontSize: 'var(--t-sm)', fontWeight: 600, flex: '0 0 auto' }}
        >{props.cancelLabel || 'Cancel'}</button>
      ) : null}
    </div>
  );
}

/**
 * OptionSelect — THE option picker. One list of `{key, label, desc?, media?,
 * motionSrc?, swatch?, value?, payload?}`, one selection, ONE signal.
 *
 * It is one component because the three it replaced — a chip filter bar, a
 * choice-chip field and an option catalogue — held the same selection over the
 * same option shape with the same `useSelection(nodeId)` idiom, down to the
 * pill geometry, and differed in exactly two DEFAULTS and one drawing. That is
 * what props are for. A `fallback` pointing at a near-copy of the component
 * declaring it (both of the absorbed entries declared one) is the tell.
 *
 * THE TWO DEFAULTS, stated because they are opposite and a page depends on
 * which it gets:
 *   `allOption`  PREPENDS the `{key:'all'}` head a BOUND list cannot author for
 *                itself (a literal list already leading with one keeps its own
 *                label) and the control opens on it. `facetCounts` emits the
 *                chip set a feed EARNED, so without the head the busiest chip
 *                rendered active over a grid that was in fact unfiltered, with
 *                no chip back to everything. `'all'` is INERT in the grid's
 *                filter, so the head applies nothing.
 *   `clearable`  re-tapping the active option CLEARS it, and the control opens
 *                with NOTHING selected. A form field must be emptiable; an
 *                always-on filter row must never land on "nothing selected".
 * So a filter wants `allOption:true, clearable:false` and a form field wants
 * `clearable:true, allOption:false`.
 *
 * `layout` is the DRAWING, not an identity: `chips` (a pill row) · `grid` ·
 * `rail` (authored options with media and a description). A catalogue is
 * authored, not accumulated — no feed identity, no CRUD, no pending — which is
 * why it stays a control and is never wired to a collection's slots.
 *
 * `cardLayout` is the catalogue CARD inside those two: `caption` (default) is
 * the framed picture over a caption block, `poster` is the picture-dominant
 * tile with the words over a scrim — the same `PosterFace` a `Card
 * layout:"poster"` draws, so a rail and a grid of cards cannot drift apart.
 * `aspect` reaches the picture in both (`2/3` is the poster's default).
 *
 * An option's `motionSrc` is its ANIMATED cover — the preview a clip template
 * carries, drawn over that option's own still in the poster card. It is
 * optional and absent-safe: an option without one is the still it always was,
 * and a reader who asked for less motion sees the still on every option.
 *
 * `changed` carries `{key}`, the option's own `value` when it declares one,
 * and SPREADS its `payload` at the top level (keeping it nested under `payload`
 * too), which is what lets an authored option reach a typed host slot —
 * `host.openCapture {prompt}` — without the page hard-coding a prompt per
 * option. In `mode:'multi'` the key is the selected ARRAY. A clear emits the
 * same signal with a null key, so a page wires ONE connection for "the choice
 * changed", whichever way it changed.
 *
 * `trailingAction` puts ONE non-selection affordance at the end of a chip row
 * (a search icon). It emits `actionRequested` — never `changed` — so a page can
 * never mistake it for a filter dimension.
 *
 * `readOnly` is the SAME CATALOGUE WITH NOTHING TO CHOOSE — the display rail a
 * detail page draws (scene-studio's team previews: eleven crests that illustrate
 * the template and select nothing). Every other way of drawing it loses
 * something: a collection would claim a feed identity a catalogue does not have,
 * and a picker with its taps ignored is a control that lies — it lights on
 * mount, offers a 44pt target and answers nothing. So the option row keeps its
 * `layout`/`cardLayout` exactly and gives up being a control: no element role, no
 * `aria-pressed`, no tap handler, no selection written, and nothing lit unless
 * the blueprint states a `selected`. The trailing action and the free chip go
 * with it — both are affordances, and a display rail has none.
 *
 * `interaction:"activate"` is the interactive, STATELESS sibling of the
 * normal selector. A catalogue entry can still emit its complete `changed`
 * payload on every tap, but it never stores a selected key and therefore never
 * exposes `aria-pressed` or selected styling. The default is `select`, so every
 * existing filter and form field keeps its current selection contract.
 */
/** 选择行为由既有槽位驱动；横滑 contentPadding 只控制滚动内容的首尾留白。 */
export function OptionSelect({ nodeId, variant, props = {}, data, emit, slots }) {
  const bound = props.options !== undefined ? props.options : data;
  const mode = props.mode || (variant === 'multi' ? 'multi' : 'single');
  const layout = props.layout || (variant === 'grid' || variant === 'rail' ? variant : 'chips');
  const clearable = props.clearable === true;
  const readOnly = props.readOnly === true;
  // 只在可交互控件上启用一次性激活；readOnly 继续拥有最高优先级。
  const activateOnly = !readOnly && props.interaction === 'activate';
  const options = useMemo(() => {
    const list = Array.isArray(bound) ? bound.filter(Boolean) : [];
    if (!props.allOption || String(list[0]?.key) === 'all') return list;
    return [{ key: 'all', label: props.allLabel || 'All' }, ...list];
  }, [bound, props.allOption, props.allLabel]);
  const trailing = props.trailingAction || null;

  // A control that CANNOT be empty opens on its first option; one that can
  // opens empty. `selected` seeds either.
  const initial = props.selected !== undefined ? props.selected
    : (mode === 'multi' ? EMPTY_KEYS : (clearable ? null : (options[0]?.key ?? 'all')));
  const [active, setActive] = useSelection(nodeId, initial);
  const setValueSlot = slots?.setValue;

  // A pushed value SEEDS the selection; the user can still change it
  // afterwards, which a shadowing read would have prevented.
  useEffect(() => {
    if (!activateOnly && setValueSlot?.payload?.key !== undefined) setActive(setValueSlot.payload.key);
  }, [activateOnly, setValueSlot, setActive]);

  // A display rail lights NOTHING it was not told to light: the "cannot be
  // empty ⇒ opens on its first option" default is a control's rule, and on a
  // read-only catalogue it would mark option one as chosen on every page load.
  // Told to — a `selected` prop, or a `setValue` delivery — it still marks one.
  const marked = props.selected !== undefined || setValueSlot !== undefined;
  const isOn = (key) => {
    if (readOnly && !marked) return false;
    if (activateOnly) return false;
    return mode === 'multi' ? (active || []).includes(key) : String(active) === String(key);
  };

  // 选项载荷的组装只有一条路径，选择与一次性激活因此保持完全相同的信号形状。
  const emitOption = (o) => {
    const out = { key: o.key };
    /**
     * `value` 只在选项明确声明时随行；筛选 chip 的 key 本身就是数据令牌，
     * 从展示 label 合成 value 会让过滤条件静默失配。
     */
    if (o.value !== undefined) out.value = o.value;
    // payload 同时保留嵌套值并摊到顶层，确保类型化宿主槽能直接读取参数。
    if (o.payload !== undefined) {
      out.payload = o.payload ?? null;
      if (o.payload && typeof o.payload === 'object' && !Array.isArray(o.payload)) Object.assign(out, o.payload);
    }
    emit?.('changed', out);
  };

  // 点击按交互模式分流：activate 只发事件，select 才进入现有状态机。
  const pick = (o) => {
    if (readOnly) return; // display only — no selection written, no signal out
    if (activateOnly) {
      emitOption(o);
      return;
    }
    if (mode === 'multi') {
      const held = active || [];
      const next = held.includes(o.key) ? held.filter((k) => k !== o.key) : [...held, o.key];
      setActive(next);
      emit?.('changed', { key: next });
      return;
    }
    if (clearable && isOn(o.key)) {
      setActive(null);
      emit?.('changed', { key: null, value: null });
      return;
    }
    setActive(o.key);
    emitOption(o);
  };

  /**
   * THE OPTION CELL — a `<button>` while this is a control, a plain box while
   * it is a display rail. Not cosmetic: an element that carries a button role
   * and answers nothing is the case a screen reader and a thumb both get wrong,
   * and `aria-pressed` on a tile nobody can press is a false statement.
   */
  const Cell = readOnly ? 'div' : 'button';
  const cellAttrs = (o, on) => {
    if (readOnly) return { 'aria-current': on ? 'true' : undefined };
    // 一次性激活是一枚普通按钮，不伪装成可切换的 pressed 控件。
    if (activateOnly) return { type: 'button', onClick: () => pick(o) };
    return { type: 'button', 'aria-pressed': on, onClick: () => pick(o) };
  };
  const cellCursor = readOnly ? { cursor: 'default' } : null;

  if (layout === 'grid' || layout === 'rail') {
    if (!options.length) return null;
    const rail = layout === 'rail';
    // The catalogue card's SHAPE. `caption` (the default, and what every
    // shipped rail draws today) is a framed picture over a white caption
    // block; `poster` is the picture-dominant tile with the words on it, drawn
    // by the SAME face a `Card layout:"poster"` uses — one poster in the kit,
    // not one per component.
    const poster = props.cardLayout === 'poster';
    const columns = Number(props.columns) || 2;
    // A rail card's width, in the two spellings the registry declares: `width`
    // is a CSS length (a percentage of the viewport reads well on the web),
    // `cardWidth` a point count. Both kits must land on the same card, and the
    // Flutter rail only knows `cardWidth` — so a blueprint that declares just
    // `cardWidth` used to draw 156pt cards on the phone and 42%-wide ones on
    // the web, which is the registry declaring a prop one kit silently drops.
    const railWidth = props.width || (Number(props.cardWidth) ? `${Number(props.cardWidth)}px` : '42%');
    return (
      <div style={rail
        // `flex-start`, not the default `stretch`: a rail card sizes to its own
        // content and tops-align, so a short text-only card is not stretched to
        // a taller sibling (matches the Flutter rail, which drops IntrinsicHeight
        // for the same reason). Poster/media cards are aspect-fixed regardless.
        ? { display: 'flex', alignItems: 'flex-start', gap: gapValue(props.gap, 'var(--sp-2)'), overflowX: 'auto', scrollbarWidth: 'none', ...contentPadding(props.contentPadding), paddingBottom: contentPadding(props.contentPadding).paddingBottom + 2 }
        : { display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0,1fr))`, gap: gapValue(props.gap, 'var(--sp-2)') }}
      >
        {options.map((o) => {
          const on = isOn(o.key);
          const meta = optionMeta(o);
          if (poster) {
            return (
              <Cell
                key={o.key}
                {...cellAttrs(o, on)}
                style={{
                  ...resetButton,
                  ...cellCursor,
                  flex: rail ? `0 0 ${railWidth}` : undefined,
                  minWidth: 0, position: 'relative', padding: 0,
                  borderRadius: 'var(--r)', overflow: 'hidden',
                  boxShadow: 'var(--shadow-1)',
                  border: `1px solid ${on ? 'var(--accent)' : 'transparent'}`,
                }}
              >
                <PosterFace
                  src={o.media}
                  // an option that IS a clip says so with a moving cover, not
                  // only with an eyebrow: `motionSrc` is the option's animated
                  // preview, drawn over its own still by the same poster face
                  // a `Card layout:"poster"` uses.
                  motionSrc={o.motionSrc}
                  eyebrow={meta?.text}
                  eyebrowTone={meta?.tone}
                  title={o.label || o.key}
                  subtitle={o.desc}
                  alt={o.label || o.key}
                  aspect={props.aspect || POSTER_ASPECT}
                  scrim={props.scrim === undefined ? 'auto' : props.scrim}
                  overlayAlign={props.overlayAlign}
                />
              </Cell>
            );
          }
          return (
            <Cell
              key={o.key}
              {...cellAttrs(o, on)}
              style={{
                ...resetButton,
                ...cellCursor,
                flex: rail ? `0 0 ${railWidth}` : undefined,
                minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6,
                borderRadius: 'var(--r)', overflow: 'hidden',
                background: 'var(--surface)', boxShadow: 'var(--shadow-1)',
                border: `1px solid ${on ? 'var(--accent)' : 'transparent'}`,
                padding: o.media ? 0 : 'var(--sp-3)',
              }}
            >
              {o.media ? <Image props={{ src: o.media, fit: 'cover', ratio: props.aspect || '1/1' }} /> : null}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: o.media ? '0 var(--sp-3) var(--sp-3)' : 0, minWidth: 0 }}>
                {meta ? (
                  <span style={{
                    ...TYPE_STYLE.eyebrow, letterSpacing: '0.1em',
                    color: meta.tone ? toneColor(meta.tone) : 'var(--text-3)',
                    minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{meta.text}</span>
                ) : null}
                <span style={{
                  ...TYPE_STYLE.caption, fontWeight: 600, color: on ? toneColor('accent') : 'var(--text)',
                  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{o.label || o.key}</span>
                {o.desc ? (
                  <span style={{ fontSize: 'var(--t-xs)', color: 'var(--text-3)', lineHeight: 1.35 }}>{o.desc}</span>
                ) : null}
              </div>
            </Cell>
          );
        })}
      </div>
    );
  }

  // `emphasis` is the pill FILL: soft reads as narrowing a list, solid as a
  // value committed into something being composed.
  const solid = props.emphasis === 'solid';
  const chipStyle = (on) => ({
    ...resetButton,
    flex: '0 0 auto',
    padding: '7px 14px',
    minHeight: 34,
    borderRadius: 'var(--r-pill)',
    fontSize: 'var(--t-sm)',
    fontWeight: on ? 700 : 500,
    background: on ? (solid ? 'var(--accent)' : 'var(--accent-soft)') : 'var(--surface)',
    color: on ? (solid ? 'var(--accent-ink)' : 'var(--accent)') : 'var(--text-2)',
    border: `1px solid ${on ? (solid ? 'var(--accent)' : 'var(--accent-line)') : 'var(--line)'}`,
    whiteSpace: 'nowrap',
    transition: 'background 140ms var(--ease)',
  });

  return (
    <div style={{
      display: 'flex', gap: 8, flexWrap: props.wrap ? 'wrap' : 'nowrap',
      overflowX: 'auto', scrollbarWidth: 'none', ...contentPadding(props.contentPadding), paddingBottom: contentPadding(props.contentPadding).paddingBottom + 2,
    }}>
      {options.map((o) => {
        const swatch = optionSwatch(o.swatch);
        return (
          <Cell
            key={o.key}
            {...cellAttrs(o, isOn(o.key))}
            style={{ ...chipStyle(isOn(o.key)), ...cellCursor }}
          >
            {swatch ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span aria-hidden="true" style={{
                  width: 16, height: 16, flex: '0 0 16px', borderRadius: '50%',
                  background: swatch, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
                }} />
                <span>{o.label || o.key}</span>
              </span>
            ) : (o.label || o.key)}
            {o.count != null && o.count !== '' ? (
              <span data-option-count style={{ marginLeft: 6, color: 'var(--text-3)', fontWeight: 500 }}>{String(o.count)}</span>
            ) : null}
          </Cell>
        );
      })}
      {/* The free chip composes nothing itself: it emits an empty value so the
          composer beside it takes over. */}
      {props.allowFree && !readOnly ? (
        <button
          key="__free"
          type="button"
          onClick={() => {
            if (!activateOnly) setActive(null);
            emit?.('changed', { key: 'free', value: '' });
          }}
          style={{
            ...resetButton,
            flex: '0 0 auto', padding: '7px 14px', minHeight: 34, borderRadius: 'var(--r-pill)',
            fontSize: 'var(--t-sm)', fontWeight: 500, color: 'var(--text-3)',
            border: '1px dashed var(--line)', whiteSpace: 'nowrap',
          }}
        >{props.freeLabel || '+ Custom'}</button>
      ) : null}
      {trailing && !readOnly ? (
        <button
          type="button"
          aria-label={trailing.label || trailing.action || 'Action'}
          onClick={() => emit?.('actionRequested', { action: trailing.action || trailing.key || 'action' })}
          style={{
            ...resetButton,
            flex: '0 0 auto', minHeight: 34, minWidth: 34, padding: '7px 12px',
            borderRadius: 'var(--r-pill)', background: 'var(--surface)',
            border: '1px solid var(--line)', color: 'var(--text-2)',
            fontSize: 'var(--t-sm)', fontWeight: 600, whiteSpace: 'nowrap',
          }}
        >{trailing.icon === 'search' ? '⌕' : (trailing.label || '⌕')}</button>
      ) : null}
    </div>
  );
}
